import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Select,
  Typography, Row, Col, Statistic, Popconfirm, Tooltip, message, Alert,
} from 'antd';
import {
  DeleteOutlined, ReloadOutlined, CheckOutlined, StopOutlined,
  ShoppingOutlined, EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { classifiedsApi, Classified, ClassifiedStats } from '../../api/classifieds';

const { Title, Text } = Typography;

const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  sell: { label: 'Продам', color: 'blue' },
  buy: { label: 'Куплю', color: 'green' },
  service: { label: 'Услуга', color: 'purple' },
  rent: { label: 'Аренда', color: 'orange' },
  giveaway: { label: 'Отдам', color: 'cyan' },
};

const ClassifiedsList: React.FC = () => {
  const [items, setItems] = useState<Classified[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ClassifiedStats | null>(null);
  const [filterCategory, setFilterCategory] = useState<string | undefined>();
  const [filterActive, setFilterActive] = useState<boolean | undefined>();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [itemsRes, statsRes] = await Promise.allSettled([
        classifiedsApi.list({ category: filterCategory, is_active: filterActive }),
        classifiedsApi.stats(),
      ]);
      if (itemsRes.status === 'fulfilled') setItems(itemsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterActive]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleModerate = async (id: number, is_active: boolean) => {
    try {
      await classifiedsApi.moderate(id, is_active);
      messageApi.success(is_active ? 'Объявление активировано' : 'Объявление скрыто');
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка модерации');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await classifiedsApi.delete(id);
      messageApi.success('Объявление удалено');
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const columns: ColumnsType<Classified> = [
    {
      title: 'Заголовок',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {r.subcategory && <Text type="secondary" style={{ fontSize: 12 }}>{r.subcategory}</Text>}
        </Space>
      ),
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      render: (cat: string) => {
        const cfg = CATEGORY_CONFIG[cat] || { label: cat, color: 'default' };
        return <Tag>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      align: 'right',
      render: (v: number) => v ? <Text strong>{v.toLocaleString('ru-RU')} тг</Text> : <Text type="secondary">--</Text>,
    },
    {
      title: 'Автор',
      dataIndex: 'author_name',
      key: 'author_name',
      width: 140,
      ellipsis: true,
      render: (v: string) => v || <Text type="secondary">--</Text>,
    },
    {
      title: 'Просмотры',
      dataIndex: 'views_count',
      key: 'views_count',
      width: 100,
      align: 'center',
      render: (v: number) => <Space><EyeOutlined />{v}</Space>,
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      render: (active: boolean) =>
        active ? <Tag color="green">Активно</Tag> : <Tag color="red">Скрыто</Tag>,
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (_: any, record: Classified) => (
        <Space size={4}>
          <Tooltip title={record.is_active ? 'Скрыть' : 'Активировать'}>
            <Button
              size="small"
              icon={record.is_active ? <StopOutlined /> : <CheckOutlined />}
              onClick={() => handleModerate(record.id, !record.is_active)}
            />
          </Tooltip>
          <Popconfirm title="Удалить объявление?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить">
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Объявления</Title>
          <Text type="secondary">Модерация объявлений жителей города Аксай</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>
          Обновить
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Всего объявлений" value={stats?.total ?? items.length} prefix={<ShoppingOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Активных" value={stats?.active ?? 0} valueStyle={{ color: 'var(--l-ok)' }} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Скрытых" value={(stats?.total ?? 0) - (stats?.active ?? 0)} valueStyle={{ color: 'var(--l-accent)' }} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }}>
        <div style={{ padding: '16px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Select placeholder="Категория" value={filterCategory} onChange={setFilterCategory} allowClear style={{ width: 160 }}>
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
              <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
            ))}
          </Select>
          <Select placeholder="Статус" value={filterActive === undefined ? undefined : filterActive ? 'true' : 'false'} onChange={(val) => setFilterActive(val === undefined ? undefined : val === 'true')} allowClear style={{ width: 140 }}>
            <Select.Option value="true">Активные</Select.Option>
            <Select.Option value="false">Скрытые</Select.Option>
          </Select>
        </div>
        <Table<Classified> rowKey="id" columns={columns} dataSource={items} loading={loading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Объявления не найдены' }} scroll={{ x: 900 }} />
      </Card>
    </div>
  );
};

export default ClassifiedsList;
